import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/GradientCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import NewChatDialog from "../components/NewChatDialog";
import { toast } from "sonner";
import { MessageCircle, Send, UserPlus, Users, Search, ShieldCheck } from "lucide-react";

/**
 * Chat page.
 * - Left rail: thread list (1:1 DMs + groups) with unread badges.
 * - Right pane: messages + composer.
 * - Polls every 5 s for thread list + active thread messages.
 * - Audit toggle for Master Admin (read-only view of every thread in the system).
 */
const POLL_MS = 5000;

// ── Chat avatar helpers ────────────────────────────────────────────
// Deterministic tone from the seed's first letter so each teammate
// gets a stable colour across sessions.
const LETTER_TONES = [
  "text-sky-400",     "text-emerald-400", "text-rose-400", "text-amber-400",
  "text-violet-400",  "text-cyan-400",    "text-lime-400", "text-fuchsia-400",
];
function letterTone(seed) {
  const s = String(seed || "?").toUpperCase().charCodeAt(0) || 65;
  return LETTER_TONES[(s - 65 + LETTER_TONES.length) % LETTER_TONES.length];
}

// Compact glowing letter used inline in message bubbles and thread rows.
// No background — pure text with a soft glow that reads as morphed light.
function ChatLetter({ name, size = 26, tone }) {
  const letter = (name || "?").charAt(0).toUpperCase();
  const colour = tone === "indigo" ? "text-indigo-300" : letterTone(name);
  return (
    <span
      className={`flex-shrink-0 grid place-items-center font-extrabold heading select-none ${colour}`}
      style={{
        width: size,
        height: size,
        fontSize: size * 0.78,
        lineHeight: 1,
        textShadow:
          "0 0 5px currentColor, 0 0 12px currentColor, 0 0 22px rgba(129,140,248,0.5)",
      }}
      aria-hidden="true"
      data-keep-color
    >
      {letter}
    </span>
  );
}

// Top-bar avatar — larger glowing letter with an optional "group" indicator.
// Sits directly on the glass banner, no solid disc.
function ChatAvatar({ name, isGroup, size = 40 }) {
  const letter = (name || "?").charAt(0).toUpperCase();
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      {isGroup ? (
        <Users
          size={size * 0.85}
          className="text-white"
          style={{ filter: "drop-shadow(0 0 8px rgba(255,255,255,0.75)) drop-shadow(0 0 18px rgba(129,140,248,0.55))" }}
          data-keep-color
        />
      ) : (
        <span
          className="absolute inset-0 grid place-items-center text-white font-extrabold heading"
          style={{
            fontSize: size * 0.86,
            lineHeight: 1,
            textShadow: "0 0 6px rgba(255,255,255,0.85), 0 0 16px rgba(255,255,255,0.55), 0 0 32px rgba(129,140,248,0.6)",
          }}
          data-keep-color
        >
          {letter}
        </span>
      )}
    </div>
  );
}

export default function Chat({ embedded = false }) {
  const { user } = useAuth();
  const isMaster = user?.role === "master_admin";

  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [auditMode, setAuditMode] = useState(false);
  const [search, setSearch] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const loadThreads = async () => {
    try {
      const { data } = await api.get(auditMode ? "/chat/admin/all-threads" : "/chat/threads");
      setThreads(data || []);
    } catch (e) {
      if (e.response?.status !== 403) toast.error(formatError(e.response?.data?.detail));
    }
  };
  const loadMessages = async (tid) => {
    if (!tid) return;
    try {
      const { data } = await api.get(`/chat/threads/${tid}/messages`);
      setMessages(data || []);
      // Mark as read (only if I'm a member — audit calls are silently ignored)
      api.post(`/chat/threads/${tid}/read`).catch(() => {});
    } catch (e) {
      if (e.response?.status !== 403) toast.error(formatError(e.response?.data?.detail));
    }
  };

  useEffect(() => { loadThreads(); }, [auditMode]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadMessages(activeId); }, [activeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling — refresh thread list + current thread every 5s
  useEffect(() => {
    const t = setInterval(() => {
      loadThreads();
      if (activeId) loadMessages(activeId);
    }, POLL_MS);
    return () => clearInterval(t);
  }, [activeId, auditMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, activeId]);

  const filteredThreads = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter((t) => {
      const names = Object.values(t.member_names || {}).join(" ").toLowerCase();
      return (t.name || "").toLowerCase().includes(q) || names.includes(q) || (t.last_message_text || "").toLowerCase().includes(q);
    });
  }, [threads, search]);

  const activeThread = threads.find((t) => t.id === activeId);
  const otherMember = (t) => {
    if (!t || t.type !== "dm") return "";
    const otherId = (t.member_ids || []).find((mid) => mid !== user?.id);
    return (t.member_names || {})[otherId] || otherId || "Unknown";
  };
  const threadTitle = (t) => t?.type === "group" ? (t?.name || "Group") : otherMember(t);
  const threadSubtitle = (t) => {
    if (!t) return "";
    if (t.type === "group") return `${(t.member_ids || []).length} members`;
    return "Direct message";
  };

  const send = async () => {
    const text = draft.trim();
    if (!text || !activeId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/chat/threads/${activeId}/messages`, { text });
      setMessages((m) => [...m, data]);
      setDraft("");
      // Refresh thread list so last_message_text updates
      loadThreads();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="space-y-4">
      {!embedded && (
        <PageHeader
          moduleKey="chat"
          title="Chat"
          eyebrow="Workspace"
          subtitle="Text-only messaging between teammates. Messages auto-delete after 60 days."
          icon={MessageCircle}
          actions={
            <div className="flex items-center gap-2">
              {isMaster && (
                <Button
                  variant={auditMode ? "default" : "outline"}
                  className={auditMode ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}
                  onClick={() => { setAuditMode((v) => !v); setActiveId(null); }}
                  data-testid="chat-audit-toggle"
                >
                  <ShieldCheck size={14} className="mr-1.5" /> {auditMode ? "Exit Audit" : "Audit View"}
                </Button>
              )}
              <Button onClick={() => setNewOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="chat-new-btn">
                <UserPlus size={14} className="mr-1.5" /> New Chat
              </Button>
            </div>
          }
        />
      )}
      {embedded && (
        <div className="flex items-center justify-end gap-2">
          {isMaster && (
            <Button
              variant={auditMode ? "default" : "outline"}
              className={auditMode ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}
              onClick={() => { setAuditMode((v) => !v); setActiveId(null); }}
              data-testid="chat-audit-toggle"
            >
              <ShieldCheck size={14} className="mr-1.5" /> {auditMode ? "Exit Audit" : "Audit View"}
            </Button>
          )}
          <Button onClick={() => setNewOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="chat-new-btn">
            <UserPlus size={14} className="mr-1.5" /> New Chat
          </Button>
        </div>
      )}

      {auditMode && (
        <div className="rounded-md border border-rose-200 bg-rose-50/40 px-3 py-2 text-[11.5px] text-rose-900 flex items-start gap-2">
          <ShieldCheck size={14} className="mt-0.5 shrink-0" />
          <div><strong>Audit mode.</strong> You can read every thread in the system but cannot send messages unless you are a member.</div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-3 h-[68vh]">
        {/* Thread list */}
        <div className="pk-card overflow-hidden flex flex-col">
          <div className="p-3 border-b border-slate-200">
            <div className="relative">
              <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search chats…" className="h-9 pl-7 text-sm" data-testid="chat-search" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredThreads.length === 0 && (
              <div className="text-center py-8 px-3 text-xs text-slate-400 italic">
                {auditMode ? "No threads yet." : "Click \"New Chat\" to start a conversation."}
              </div>
            )}
            {filteredThreads.map((t) => {
              const active = t.id === activeId;
              const title = threadTitle(t);
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveId(t.id)}
                  className={`w-full text-left px-3 py-2.5 border-b border-slate-100 transition-colors ${active ? "bg-indigo-50 border-l-4 border-l-indigo-500" : "hover:bg-slate-50"}`}
                  data-testid={`chat-thread-${t.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <ChatLetter name={t.type === "group" ? "" : title} size={28} tone={active ? "indigo" : ""} />
                      {t.type === "group" && (
                        <Users
                          size={16}
                          className={active ? "text-indigo-400" : "text-slate-400"}
                          style={{ filter: "drop-shadow(0 0 6px currentColor)" }}
                          data-keep-color
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-[13px] text-slate-900 dark:text-slate-100 truncate">{title}</div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                          {t.last_message_by_name && <span className="text-slate-600 dark:text-slate-300 font-medium">{t.last_message_by_name}: </span>}
                          {t.last_message_text || (t.type === "group" ? threadSubtitle(t) : "Direct message")}
                        </div>
                      </div>
                    </div>
                    {t.unread && !auditMode && (<Badge className="bg-rose-500 text-white text-[9px] hover:opacity-90 shadow-[0_0_10px_rgba(244,63,94,0.6)]" data-keep-color>new</Badge>)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Conversation */}
        <div className="pk-card flex flex-col overflow-hidden">
          {!activeThread ? (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm italic">
              Select a chat to start messaging.
            </div>
          ) : (
            <>
              {/* Top bar — morphed glass with a themed glowing accent bar.
                  v109.9.87 — no solid gradient; icons/letters carry the glow. */}
              <div
                className="relative px-4 py-3 border-b border-white/20 dark:border-white/10 flex items-center justify-between gap-2 overflow-hidden"
                style={{
                  background:
                    "linear-gradient(120deg, rgba(59,130,246,0.35), rgba(99,102,241,0.32) 45%, rgba(139,92,246,0.32))",
                  backdropFilter: "blur(14px) saturate(140%)",
                  WebkitBackdropFilter: "blur(14px) saturate(140%)",
                }}
                data-keep-color
              >
                {/* Glowing accent bar aligned to app theme */}
                <span
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-[2px]"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent, rgba(129,140,248,0.9) 30%, rgba(232,121,249,0.9) 70%, transparent)",
                    filter: "blur(1px)",
                    boxShadow: "0 0 14px rgba(129,140,248,0.55)",
                  }}
                  data-keep-color
                  aria-hidden="true"
                />
                <div className="flex items-center gap-3 min-w-0 relative">
                  <ChatAvatar
                    name={threadTitle(activeThread)}
                    isGroup={activeThread.type === "group"}
                    size={40}
                  />
                  <div className="min-w-0">
                    <div
                      className="font-bold text-white truncate heading"
                      style={{ textShadow: "0 0 6px rgba(255,255,255,0.5), 0 0 18px rgba(129,140,248,0.55)" }}
                      data-keep-color
                    >
                      {threadTitle(activeThread)}
                    </div>
                    <div className="text-[11px] text-white/85 truncate" data-keep-color>{threadSubtitle(activeThread)}</div>
                  </div>
                </div>
                {activeThread.type === "group" && (
                  <div className="hidden md:flex items-center gap-1 text-[11px] max-w-[380px] overflow-hidden relative" data-keep-color>
                    {Object.values(activeThread.member_names || {}).slice(0, 4).map((n, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] text-white/95 border border-white/25"
                        style={{ background: "rgba(255,255,255,0.10)" }}
                        data-keep-color
                      >
                        <ChatLetter name={n} size={13} />
                        <span className="truncate max-w-[70px]">{n}</span>
                      </span>
                    ))}
                    {(activeThread.member_ids || []).length > 4 && (
                      <span className="text-white/90" data-keep-color>+{(activeThread.member_ids || []).length - 4}</span>
                    )}
                  </div>
                )}
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/30 dark:bg-slate-950/40" data-testid="chat-messages">
                {messages.length === 0 && (<div className="text-center text-xs text-slate-400 italic py-10">No messages yet — say hi 👋</div>)}
                {messages.map((m) => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div key={m.id} className={`flex items-end gap-2 ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                      {!mine && <ChatLetter name={m.sender_name} size={26} />}
                      <div className={`max-w-[72%] rounded-2xl px-3 py-2 text-[13px] ${mine ? "bg-indigo-600 text-white rounded-tr-sm" : "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded-tl-sm"}`}>
                        {!mine && activeThread.type === "group" && (<div className="text-[10px] font-semibold opacity-70 mb-0.5">{m.sender_name}</div>)}
                        <div className="whitespace-pre-wrap break-words">{m.text}</div>
                        <div className={`text-[9.5px] mt-1 ${mine ? "text-indigo-100" : "text-slate-400"}`}>
                          {m.created_at ? new Date(m.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : ""}
                        </div>
                      </div>
                      {mine && <ChatLetter name={user?.name} size={26} tone="indigo" />}
                    </div>
                  );
                })}
              </div>
              {!auditMode && (activeThread.member_ids || []).includes(user?.id) && (
                <div className="border-t border-slate-200 p-3 flex items-end gap-2">
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onKey}
                    placeholder="Type a message… (Enter to send, Shift+Enter for newline)"
                    rows={1}
                    className="flex-1 resize-none text-sm border border-slate-200 rounded-lg px-3 py-2 min-h-[40px] max-h-[120px] bg-white dark:bg-slate-900 dark:border-slate-700"
                    data-testid="chat-draft"
                  />
                  <Button onClick={send} disabled={sending || !draft.trim()} className="h-10 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="chat-send">
                    <Send size={14} className="mr-1" /> Send
                  </Button>
                </div>
              )}
              {auditMode && (
                <div className="border-t border-slate-200 p-3 text-[11px] text-slate-500 italic text-center bg-slate-50/60">
                  Audit mode — read only.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <NewChatDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(t) => { setActiveId(t.id); loadThreads(); }} />
    </div>
  );
}

